import React, { useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import MainLayout from "../layouts/MainLayout";
import Spinner from "../components/DNVSpinner";
import { ApplicationState } from "../store";
import { Button, Divider, List, Tabs } from "antd";

import {
  approveTestSession,
  getSessionSummary,
  setIsDownloading,
  openCommentModal,
} from "../store/slices/sessionSummarySlice";
import {
  DowloadSessionFilesForInspection,
  DownloadAttestationOfConformity,
  DownloadReviewOutcome,
} from "../store/api";
import FileDownload from "js-file-download";
import SessionRunResult from "../components/Results/SessionRunResults";
import SessionCommentModal from "../components/SessionCommentModal";
import SelectionsSummary from "../components/Summary/SelectionsSummary";
import StatusHistoryTable from "../components/Summary/StatusHistoryTable";
import SelectedTestCasesTable from "../components/Summary/SelectedTestCasesTable";

const SessionSummaryPage = (props: any) => {
  const dispatch = useDispatch();
  const sessionId = props.match.params.sessionId;

  const fetchInitial = useCallback(
    () => dispatch(getSessionSummary(sessionId)),
    [dispatch, sessionId]
  );

  useEffect(() => {
    fetchInitial();
  }, [fetchInitial]);

  const { TabPane } = Tabs;

  const {
    isLoading,
    isDownloading,
    choices,
    session,
    results,
    comments,
    user,
    hasFilesToDownload,
    history,
    declarationOfConformity,
  } = useSelector((state: ApplicationState) => state.sessionSummarySlice);

  const currentUser = useSelector(
    (state: ApplicationState) => state.init.data.user
  );

  const approveSessionHandler = useCallback(
    (sessionId) => dispatch(approveTestSession(sessionId)),
    [dispatch]
  );

  const openModalHandler = useCallback(
    (destinationStatus: string, placeholder: string) =>
      dispatch(openCommentModal(destinationStatus, placeholder)),
    [dispatch]
  );

  const DowloadFilesForInspection = useCallback(
    (sessionId, sessionName) => {
      dispatch(setIsDownloading(true));
      DowloadSessionFilesForInspection(sessionId)
        .then((response: Blob) => {
          if (response && response.size > 0)
            FileDownload(response, `${sessionName}_${Date.now()}.zip`);
        })
        .finally(() => dispatch(setIsDownloading(false)));
    },
    [dispatch]
  );

  const DownloadAttestation = useCallback(
    (sessionId, sessionName) => {
      dispatch(setIsDownloading(true));
      DownloadAttestationOfConformity(sessionId)
        .then((response: Blob) => {
          if (response && response.size > 0)
            FileDownload(
              response,
              `Attestation_${sessionName}_${Date.now()}.docx`
            );
        })
        .finally(() => dispatch(setIsDownloading(false)));
    },
    [dispatch]
  );

  const DownloadReview = useCallback(
    (sessionId, sessionName) => {
      dispatch(setIsDownloading(true));
      DownloadReviewOutcome(sessionId)
        .then((response: Blob) => {
          if (response && response.size > 0)
            FileDownload(response, `Review_${sessionName}_${Date.now()}.docx`);
        })
        .finally(() => dispatch(setIsDownloading(false)));
    },
    [dispatch]
  );

  return (
    <div className="cimbion-homepage">
      <MainLayout>
        {isLoading ? (
          <Spinner />
        ) : (
          <div className="container">
            <h2>{`${session.name} (${user.name})`}</h2>
            <SessionCommentModal
              title="Add comment"
              sessionId={sessionId}
            ></SessionCommentModal>
            {hasFilesToDownload ? (
              <Button
                loading={isDownloading}
                onClick={() =>
                  DowloadFilesForInspection(session.id, session.name)
                }
              >
                Download Files
              </Button>
            ) : (
              ""
            )}
            {session.testSessionType?.declarationFormRequired ? (
              <Button
                loading={isDownloading}
                onClick={() => DownloadAttestation(session.id, session.name)}
              >
                Download Attestation of Conformity
              </Button>
            ) : (
              ""
            )}
            {session.testSessionType?.declarationFormRequired &&
            [
              "failed",
              "success",
              "reviewed",
              "verified",
              "sent",
              "certified",
            ].some((short) => short === session.status?.short) ? (
              <Button
                loading={isDownloading}
                onClick={() => DownloadReview(session.id, session.name)}
              >
                Download Reveiw Outcome
              </Button>
            ) : (
              ""
            )}
            {session.status?.short === "assist" ? (
              <>
                <Button onClick={() => approveSessionHandler(session.id)}>
                  Approve
                </Button>
              </>
            ) : (
              ""
            )}
            {session.status?.short === "verified" ? (
              <>
                <Button onClick={() => openModalHandler("sent", "Comment...")}>
                  Send
                </Button>
              </>
            ) : (
              ""
            )}
            {session.status?.short === "sent" ? (
              <>
                <Button
                  onClick={() =>
                    openModalHandler("certified", "Certification comment...")
                  }
                >
                  Pass certification
                </Button>
                <Button
                  onClick={() =>
                    openModalHandler(
                      "failed",
                      "State reason for certification failure..."
                    )
                  }
                >
                  Fail certification
                </Button>
              </>
            ) : (
              ""
            )}
            {session.status?.short === "success" ? (
              <>
                <Button
                  onClick={() =>
                    openModalHandler("reviewed", "Review comment...")
                  }
                >
                  Pass review
                </Button>
                <Button
                  onClick={() =>
                    openModalHandler(
                      "failed",
                      "State reason for review failure..."
                    )
                  }
                >
                  Fail review
                </Button>
              </>
            ) : (
              ""
            )}
            {session.status?.short === "reviewed" &&
            session.reviewedById !== currentUser?.id ? (
              <>
                <Button
                  onClick={() =>
                    openModalHandler("verified", "Verification comment...")
                  }
                >
                  Pass verification
                </Button>
                <Button
                  onClick={() =>
                    openModalHandler(
                      "failed",
                      "State reason for verification failure..."
                    )
                  }
                >
                  Fail verification
                </Button>
              </>
            ) : (
              ""
            )}
            <Divider></Divider>
            <Tabs>
              <TabPane tab="Selections" key="1">
                <SelectionsSummary choices={choices}></SelectionsSummary>
              </TabPane>
              <TabPane tab="Test cases" key="2">
                <SelectedTestCasesTable
                  choices={choices}
                ></SelectedTestCasesTable>
              </TabPane>
              {results.length > 0 ? (
                <TabPane tab="Session results" key="3">
                  <SessionRunResult
                    results={results}
                    sessionId={sessionId}
                    enableReruns={false}
                    downloading={false}
                    reruns={[]}
                  ></SessionRunResult>
                </TabPane>
              ) : (
                ""
              )}
              {session.testSessionType?.declarationFormRequired ? (
                <TabPane tab="Declaration of Conformity" key="4">
                  <h3>Name: {declarationOfConformity?.name}</h3>
                  <h3>Title: {declarationOfConformity?.title}</h3>
                  <h3>
                    Title function: {declarationOfConformity?.titleFunction}
                  </h3>
                  <h3>Company: {declarationOfConformity?.company}</h3>
                  <h3>Country: {declarationOfConformity?.country}</h3>
                  <h3>Address: {declarationOfConformity?.address}</h3>
                  <h3>
                    Corporate number: {declarationOfConformity?.corporateNumber}
                  </h3>
                  <h3>
                    Application name: {declarationOfConformity?.applicationName}
                  </h3>
                  <h3>Version: {declarationOfConformity?.version}</h3>
                </TabPane>
              ) : (
                ""
              )}
              <TabPane tab="History" key="5">
                <StatusHistoryTable
                  history={history}
                  user={user}
                  session={session}
                ></StatusHistoryTable>
              </TabPane>
              {comments.length > 0 ? (
                <TabPane tab="Comments" key="6">
                  <List
                    itemLayout="vertical"
                    size="large"
                    dataSource={comments}
                    renderItem={(comment) => {
                      var dateObj = new Date(comment.createdAt ?? "");
                      return (
                        <List.Item key={comment.id}>
                          <List.Item.Meta
                            title={`${comment.createdBy?.name} ${
                              comment.originStatus && comment.destinationStatus
                                ? ` (${comment.originStatus?.name} → ${comment.destinationStatus?.name})`
                                : ""
                            }`}
                            description={dateObj.toLocaleString()}
                          />
                          {comment.message}
                        </List.Item>
                      );
                    }}
                  />
                  ,
                </TabPane>
              ) : (
                ""
              )}
            </Tabs>
          </div>
        )}
      </MainLayout>
    </div>
  );
};

export default SessionSummaryPage;
